'use client'

import { sortSizesFromStrings } from '@/lib/helpers/sort-size.helper'
import { cn } from '@/lib/utils'
import { TCardFacet, TFacet } from '@/types/collection'
import { useMemo } from 'react'
import { FilterValues } from '../../../types/filter-store-v2.types'
import { shouldAddNofollowToFilterLinks } from '../../../utils/filter-utils'
import { buildFilterUrl } from './filter-url-builder'

type Props = {
  onFilterChange: (values: string[]) => void
  defaultValue?: string[]
  sizeList: string[]
  gaTrackingValue?: string
  gaTrackingLabel?: string
  originalSlug?: string
  currentFilters?: FilterValues
  facets?: TFacet[]
  cardFacets?: TCardFacet[]
}

export const FilterSizes = ({
  onFilterChange,
  defaultValue = [],
  sizeList,
  gaTrackingValue,
  gaTrackingLabel,
  originalSlug,
  currentFilters = {},
  facets,
  cardFacets,
}: Props) => {
  // Normalize defaultValue to lowercase array for consistent comparison
  const normalizedDefaultValue = useMemo(() => {
    if (!defaultValue || defaultValue.length === 0) return []
    return defaultValue
      .map((s) => String(s).trim().toLowerCase())
      .filter((s) => s.length > 0)
  }, [defaultValue])

  const handleSizeClick = (size: string) => {
    const sizeLower = size.toLowerCase()
    const newSizes = normalizedDefaultValue.includes(sizeLower)
      ? normalizedDefaultValue.filter((s) => s !== sizeLower)
      : [...normalizedDefaultValue, sizeLower]
    onFilterChange(newSizes)
  }

  const handleLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    size: string
  ) => {
    e.preventDefault()
    handleSizeClick(size)
  }

  const sizes = useMemo(() => sortSizesFromStrings(sizeList), [sizeList])

  // Check if should add nofollow
  const shouldAddNofollow = shouldAddNofollowToFilterLinks(
    currentFilters,
    facets,
    cardFacets
  )

  // Build URL for each size option
  const getFilterUrl = (size: string): string => {
    if (!originalSlug) return '#'
    const sizeLower = size.toLowerCase()
    const isChecked = normalizedDefaultValue.includes(sizeLower)
    return buildFilterUrl({
      originalSlug,
      currentFilters,
      facetKey: 'size',
      facetValue: sizeLower,
      isChecked: !isChecked, // URL for toggled state
      isMultiple: true, // Size allows multiple values
      isConcatSlug: false, // Size doesn't use concat slug
      facets,
      cardFacets,
    })
  }

  return (
    <ul className='grid grid-cols-5 gap-2 md:grid-cols-4'>
      {sizes.map((item) => {
        const filterUrl = getFilterUrl(item)
        const itemLower = item.toLowerCase()
        const isActive = normalizedDefaultValue.includes(itemLower)
        return (
          <FilterSizeItem
            key={item}
            title={item}
            isActive={isActive}
            gaTrackingValue={gaTrackingValue}
            gaTrackingLabel={gaTrackingLabel}
            filterUrl={filterUrl}
            onLinkClick={(e) => handleLinkClick(e, item)}
            shouldAddNofollow={shouldAddNofollow}
          />
        )
      })}
    </ul>
  )
}

const FilterSizeItem = ({
  title,
  isActive,
  gaTrackingValue,
  gaTrackingLabel,
  filterUrl,
  onLinkClick,
  shouldAddNofollow,
}: {
  title: string
  isActive: boolean
  gaTrackingValue?: string
  gaTrackingLabel?: string
  filterUrl: string
  onLinkClick: (e: React.MouseEvent<HTMLAnchorElement>) => void
  shouldAddNofollow: boolean
}) => {
  return (
    <li>
      <a
        href={filterUrl}
        onClick={onLinkClick}
        className={cn(
          'flex w-14 cursor-pointer items-center justify-center rounded-md border bg-light px-3 py-2 text-sm uppercase leading-4.5 no-underline transition-colors ease-linear',
          isActive
            ? 'border-primary-500 bg-primary-500 text-white'
            : 'border-neutral-300 text-neutral-500 lg:hover:border-primary lg:hover:bg-primary lg:hover:text-light'
        )}
        aria-label={`Filter by size ${title}`}
        ga-tracking-value={gaTrackingValue}
        ga-tracking-label={gaTrackingLabel}
        rel={shouldAddNofollow ? 'nofollow' : undefined}
      >
        {title}
      </a>
    </li>
  )
}
